// Direct fetch wrapper around the AWS Cognito IDP JSON 1.1 API.
// No external SDK needed, Cognito speaks plain HTTP with X-Amz-Target headers.
//
// Configure with:
//   REACT_APP_COGNITO_REGION
//   REACT_APP_COGNITO_USER_POOL_ID
//   REACT_APP_COGNITO_CLIENT_ID
//   REACT_APP_COGNITO_DOMAIN   (hosted-UI domain, enables "Continue with Google")

const REGION   = process.env.REACT_APP_COGNITO_REGION   || 'us-east-1';
const CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID || '';

const ENDPOINT = `https://cognito-idp.${REGION}.amazonaws.com/`;

// Hosted-UI domain, e.g. wavelengtherp.auth.us-east-1.amazoncognito.com
// (terraform output cognito_hosted_ui_domain). Scheme/trailing slash tolerated.
const HOSTED_UI_DOMAIN = (process.env.REACT_APP_COGNITO_DOMAIN || '')
    .trim()
    .replace(/^https?:\/\//, '')
    .replace(/\/+$/, '');

async function call(target, body) {
    const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-amz-json-1.1',
            'X-Amz-Target': `AWSCognitoIdentityProviderService.${target}`,
        },
        body: JSON.stringify(body),
    });
    let data;
    try { data = await res.json(); } catch { data = {}; }
    if (!res.ok) {
        const err = new Error(data.message || data.__type || `HTTP ${res.status}`);
        err.code = data.__type;
        throw err;
    }
    return data;
}

export const cognitoApi = {
    // Step 1 of login. Returns AuthenticationResult OR a ChallengeName (e.g. NEW_PASSWORD_REQUIRED).
    initiateAuth: (username, password) => call('InitiateAuth', {
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: CLIENT_ID,
        AuthParameters: { USERNAME: username, PASSWORD: password },
    }),

    // Used to complete the NEW_PASSWORD_REQUIRED challenge after first admin-created sign-in.
    respondToNewPasswordChallenge: (username, newPassword, session) => call('RespondToAuthChallenge', {
        ChallengeName: 'NEW_PASSWORD_REQUIRED',
        ClientId: CLIENT_ID,
        Session: session,
        ChallengeResponses: { USERNAME: username, NEW_PASSWORD: newPassword },
    }),

    // Forgot-password step 1, Cognito emails a 6-digit code.
    forgotPassword: (username) => call('ForgotPassword', {
        ClientId: CLIENT_ID,
        Username: username,
    }),

    // Forgot-password step 2, submit the code and the new password.
    confirmForgotPassword: (username, code, newPassword) => call('ConfirmForgotPassword', {
        ClientId: CLIENT_ID,
        Username: username,
        ConfirmationCode: code,
        Password: newPassword,
    }),

    // Refresh access/id tokens using a refresh token.
    refresh: (refreshToken) => call('InitiateAuth', {
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        ClientId: CLIENT_ID,
        AuthParameters: { REFRESH_TOKEN: refreshToken },
    }),

    // Optional: revoke tokens server-side. Best-effort, don't block UI on failure.
    globalSignOut: (accessToken) => call('GlobalSignOut', { AccessToken: accessToken }),

    // The signed-in person changes their own attributes (the Profile page's
    // name). [{ Name: 'given_name', Value: 'Ada' }, …]; the new values reach
    // the app with the next ID token, so the caller refreshes after.
    updateUserAttributes: (accessToken, attributes) => call('UpdateUserAttributes', {
        AccessToken: accessToken,
        UserAttributes: attributes,
    }),

    // ── Self-serve signup (the /signup wizard) ────────────────────────────────
    // Creates an UNCONFIRMED user; Cognito emails a 6-digit verification code
    // (auto_verified_attributes = email on the pool).
    signUp: (email, password, firstName, lastName) => call('SignUp', {
        ClientId: CLIENT_ID,
        Username: email,
        Password: password,
        UserAttributes: [
            { Name: 'email', Value: email },
            { Name: 'given_name', Value: firstName },
            { Name: 'family_name', Value: lastName },
        ],
    }),

    // Submit the emailed code, flips the user to CONFIRMED so they can sign in.
    confirmSignUp: (email, code) => call('ConfirmSignUp', {
        ClientId: CLIENT_ID,
        Username: email,
        ConfirmationCode: code,
    }),

    // Re-send the verification code (throttled by Cognito).
    resendConfirmationCode: (email) => call('ResendConfirmationCode', {
        ClientId: CLIENT_ID,
        Username: email,
    }),
};

// ── Hosted UI, OAuth 2.0 authorization code + PKCE ("Continue with Google") ──
// The login page sends the browser to the pool's hosted-UI domain with
// identity_provider=Google; Cognito bounces straight to Google (the user never
// sees a Cognito page), then back to our /login with ?code=…&state=…. We swap
// the code for the SAME user-pool tokens the password flow yields, so the rest
// of the app (API Gateway authorizer included) can't tell the two apart.
//
// PKCE: the SPA has no client secret, so the code is bound to a one-time
// verifier kept in sessionStorage across the redirect.

function base64Url(bytes) {
    let bin = '';
    bytes.forEach((b) => { bin += String.fromCharCode(b); });
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomToken(byteLength = 32) {
    const bytes = new Uint8Array(byteLength);
    (window.crypto || window.msCrypto).getRandomValues(bytes);
    return base64Url(bytes);
}

// { verifier, challenge, method }. Falls back to `plain` only where WebCrypto
// is unavailable (non-secure http origins), Cognito accepts both.
export async function createPkcePair() {
    const verifier = randomToken(32);
    const subtle   = window.crypto?.subtle;
    if (!subtle) return { verifier, challenge: verifier, method: 'plain' };
    const digest = await subtle.digest('SHA-256', new TextEncoder().encode(verifier));
    return { verifier, challenge: base64Url(new Uint8Array(digest)), method: 'S256' };
}

export const hostedUi = {
    domain:  HOSTED_UI_DOMAIN,
    enabled: !!(HOSTED_UI_DOMAIN && CLIENT_ID),

    // Random, URL-safe nonce for the OAuth `state` parameter.
    newState: () => randomToken(16),

    // Full authorize URL. `identityProvider` = "Google" (or omit for the hosted login page).
    authorizeUrl({ identityProvider, redirectUri, state, codeChallenge, codeChallengeMethod = 'S256', scope = 'openid email profile' }) {
        const params = new URLSearchParams({
            client_id:     CLIENT_ID,
            response_type: 'code',
            scope,
            redirect_uri:  redirectUri,
            state,
        });
        if (identityProvider) params.set('identity_provider', identityProvider);
        if (codeChallenge) {
            params.set('code_challenge', codeChallenge);
            params.set('code_challenge_method', codeChallengeMethod);
        }
        return `https://${HOSTED_UI_DOMAIN}/oauth2/authorize?${params.toString()}`;
    },

    // Ends the hosted-UI session (so the next "Continue with Google" on a shared
    // machine doesn't silently resume the previous person) and lands on `logoutUri`,
    // which must be one of the app client's registered logout URLs.
    logoutUrl(logoutUri) {
        const params = new URLSearchParams({ client_id: CLIENT_ID, logout_uri: logoutUri });
        return `https://${HOSTED_UI_DOMAIN}/logout?${params.toString()}`;
    },

    // Authorization code → tokens. Resolves { id_token, access_token, refresh_token, expires_in }.
    // Rejects with err.code = the OAuth error ("invalid_grant" for a used/expired code).
    async exchangeCode({ code, redirectUri, codeVerifier }) {
        const body = new URLSearchParams({
            grant_type:   'authorization_code',
            client_id:    CLIENT_ID,
            code,
            redirect_uri: redirectUri,
        });
        if (codeVerifier) body.set('code_verifier', codeVerifier);

        const res = await fetch(`https://${HOSTED_UI_DOMAIN}/oauth2/token`, {
            method:  'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body:    body.toString(),
        });
        let data;
        try { data = await res.json(); } catch { data = {}; }
        if (!res.ok || !data.id_token) {
            const err = new Error(data.error_description || data.error || `HTTP ${res.status}`);
            err.code = data.error || `http_${res.status}`;
            throw err;
        }
        return data;
    },
};

// Decode a Cognito ID token's claims (no signature verification, that happens server-side).
export function decodeIdToken(idToken) {
    if (!idToken) return null;
    try {
        const payload = idToken.split('.')[1];
        const json    = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
        return JSON.parse(decodeURIComponent(escape(json)));
    } catch {
        return null;
    }
}
